/**
 * Shadow binary link installation utilities for Socket CLI.
 * Manages installation of shadow binaries for package managers.
 *
 * Key Functions:
 * - installNpmLinks: Install shadow links for npm binary
 * - installNpxLinks: Install shadow links for npx binary
 * - installPnpmLinks: Install shadow links for pnpm binary
 * - installYarnLinks: Install shadow links for yarn binary
 *
 * Shadow Installation:
 * - Creates symlinks/cmd-shims to intercept package manager commands
 * - Modifies PATH to prioritize shadow binaries
 * - Skips installation in temporary execution contexts
 *
 * Security Integration:
 * - Enables security scanning before package operations
 * - Transparent interception of package manager commands
 * - Preserves original binary functionality
 */

import path from 'node:path'
import { fileURLToPath } from 'node:url'

import cmdShim from 'cmd-shim'

import constants from '../constants.mts'
import { shouldSkipShadow } from './dlx-detection.mts'
import {
  getNpmBinPath,
  getNpxBinPath,
  isNpmBinPathShadowed,
  isNpxBinPathShadowed,
} from './npm-paths.mts'
import { getPnpmBinPath, isPnpmBinPathShadowed } from './pnpm-paths.mts'
import { getYarnBinPath, isYarnBinPathShadowed } from './yarn-paths.mts'

const __filename = fileURLToPath(import.meta.url)
const __dirname = path.dirname(__filename)

export async function installNpmLinks(shadowBinPath: string): Promise<string> {
  // Find npm being shadowed by this process.
  const binPath = getNpmBinPath()
  const { WIN32 } = constants

  // Skip shadow installation when in temporary execution context or when required for Windows.
  if (shouldSkipShadow(binPath, { cwd: __dirname, win32: WIN32 })) {
    return binPath
  }
  const shadowed = isNpmBinPathShadowed()
  // Move our bin directory to front of PATH so its found first.
  if (!shadowed) {
    if (WIN32) {
      await cmdShim(
        path.join(constants.distPath, 'npm-cli.js'),
        path.join(shadowBinPath, 'npm'),
      )
    }
    const { env } = process
    env['PATH'] = `${shadowBinPath}${path.delimiter}${env['PATH']}`
  }
  return binPath
}

export async function installNpxLinks(shadowBinPath: string): Promise<string> {
  // Find npx being shadowed by this process.
  const binPath = getNpxBinPath()
  const { WIN32 } = constants

  // Skip shadow installation when in temporary execution context or when required for Windows.
  if (shouldSkipShadow(binPath, { cwd: __dirname, win32: WIN32 })) {
    return binPath
  }
  const shadowed = isNpxBinPathShadowed()
  // Move our bin directory to front of PATH so its found first.
  if (!shadowed) {
    if (WIN32) {
      await cmdShim(
        path.join(constants.distPath, 'npx-cli.js'),
        path.join(shadowBinPath, 'npx'),
      )
    }
    const { env } = process
    env['PATH'] = `${shadowBinPath}${path.delimiter}${env['PATH']}`
  }
  return binPath
}

export async function installPnpmLinks(shadowBinPath: string): Promise<string> {
  // Find pnpm being shadowed by this process.
  const binPath = getPnpmBinPath()
  const { WIN32 } = constants

  // Skip shadow installation when in temporary execution context or when required for Windows.
  if (shouldSkipShadow(binPath, { cwd: __dirname, win32: WIN32 })) {
    return binPath
  }

  const shadowed = isPnpmBinPathShadowed()

  // Move our bin directory to front of PATH so its found first.
  if (!shadowed) {
    if (WIN32) {
      await cmdShim(
        path.join(constants.distPath, 'pnpm-cli.js'),
        path.join(shadowBinPath, 'pnpm'),
      )
    }
    const { env } = process
    env['PATH'] = `${shadowBinPath}${path.delimiter}${env['PATH']}`
  }

  return binPath
}

export async function installYarnLinks(shadowBinPath: string): Promise<string> {
  const binPath = getYarnBinPath()
  const { WIN32 } = constants

  // Skip shadow installation when in temporary execution context or when required for Windows.
  if (shouldSkipShadow(binPath, { cwd: __dirname, win32: WIN32 })) {
    return binPath
  }

  const shadowed = isYarnBinPathShadowed()

  if (!shadowed) {
    if (WIN32) {
      await cmdShim(
        path.join(constants.distPath, 'yarn-cli.js'),
        path.join(shadowBinPath, 'yarn'),
      )
    }
    const { env } = process
    env['PATH'] = `${shadowBinPath}${path.delimiter}${env['PATH']}`
  }

  return binPath
}
